/**
 * In-context upgrade card (PR 6e).
 *
 * Two surfaces use it:
 *   1. ``BillingBudgetBanner`` — at 80%+ usage on a finite-budget plan.
 *      Pushes the user to the next tier rather than the generic "Top up"
 *      CTA. Conversion happens at the moment of awareness, not after a
 *      detour through the pricing page.
 *   2. The 402 ``IncludedTokensExceededError`` deny envelope — when a
 *      run is blocked, the card sits inline in the room / runner so the
 *      user can clear the block in one click.
 *
 * Hidden cases (returns ``null``):
 *   * No current plan / unknown plan + no purchasable plans available.
 *   * Already on Ultra (top finite tier) — no next finite plan to suggest.
 *     Caller should fall back to the existing "View plans" or
 *     "Manage subscription" affordances.
 *   * Caller passes ``allowed=false`` (RBAC).
 *
 * Click → ``createSubscriptionCheckout(next.key)`` →
 * Stripe Checkout for the higher plan. Stripe handles proration; our
 * ``customer.subscription.updated`` webhook restamps the entitlement
 * row when the user lands back via Checkout's success_url.
 *
 * Important: this is NOT a Portal flow. Portal would let them browse
 * any change; we want a one-click upgrade-to-the-next-tier path that
 * matches the master plan's "decision moments where context wins
 * conversions" §M3.5 principle.
 */
import React, { useCallback, useState } from 'react';
import {
  GENERATED_PLAN_CATALOG,
  nextFiniteTierAfter,
} from '@stepflow/ui-shared/services/generatedPricing';
import { createSubscriptionCheckout, isBillingUiEnabled } from '@/services/stripeBilling';
import { useToast } from '@stepflow/ui-shell/context/ToastContext.jsx';

function formatPoints(n) {
  if (typeof n !== 'number' || !isFinite(n)) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(n);
}

function formatPriceCents(cents) {
  if (typeof cents !== 'number') return null;
  // Whole-dollar plans render without ".00".
  return cents % 100 === 0
    ? `$${cents / 100}`
    : `$${(cents / 100).toFixed(2)}`;
}

const TONE = {
  warning: {
    wrap: 'border-amber-300/80 bg-amber-50/70',
    title: 'text-amber-950',
    body: 'text-amber-900/90',
    btn:
      'inline-flex items-center rounded-lg bg-amber-800 px-3 py-1.5 text-sm font-medium ' +
      'text-white shadow-sm transition hover:bg-amber-900 disabled:opacity-50',
  },
  blocked: {
    wrap: 'border-rose-300/80 bg-rose-50/70',
    title: 'text-rose-950',
    body: 'text-rose-900/90',
    btn:
      'inline-flex items-center rounded-lg bg-rose-700 px-3 py-1.5 text-sm font-medium ' +
      'text-white shadow-sm transition hover:bg-rose-800 disabled:opacity-50',
  },
};

/**
 * Render a one-liner comparing the user's actual usage history to the
 * suggested plan's monthly headroom. Pure function — no side effects,
 * easy to unit-test.
 *
 *   - Bootstrap (no history rows yet): null. Caller falls back to
 *     plan-spec copy.
 *   - History present but never over the suggested plan's budget:
 *     "Last N months: A / B / C pts. <NextPlan> (D) covers all of them."
 *   - History present and K months exceeded the suggested plan's budget:
 *     "Last N months: A / B / C pts. K of N would have exceeded
 *     <NextPlan>'s budget — keep your current plan or look at higher tiers."
 *     (We never silently push a plan that wouldn't have worked.)
 */
export function buildHistoryPreview(history, nextPlan) {
  if (!history || !Array.isArray(history) || history.length === 0) return null;
  if (!nextPlan || typeof nextPlan.points_per_period !== 'number') return null;
  const finite = history.filter((m) => typeof m?.points === 'number');
  if (finite.length === 0) return null;
  const seq = finite.map((m) => m.points);
  const formatted = seq.map(formatPoints).join(' / ');
  const budget = nextPlan.points_per_period;
  const overCount = seq.filter((p) => p > budget).length;
  const headroom = `${formatPoints(budget)} pts / month`;
  if (overCount === 0) {
    return `Last ${seq.length} month${seq.length === 1 ? '' : 's'}: ${formatted} pts. ${nextPlan.name} (${headroom}) would have covered all of them.`;
  }
  return `Last ${seq.length} month${seq.length === 1 ? '' : 's'}: ${formatted} pts. ${overCount} of ${seq.length} would have exceeded ${nextPlan.name}'s ${headroom} — consider a higher tier.`;
}

/**
 * @param {object} props
 * @param {string|null} props.currentPlanKey - the org's current plan
 *   from ``OrgEntitlement.PlanKey``. ``null`` is fine (treated as "no
 *   plan" → suggest the lowest finite tier).
 * @param {'warning'|'blocked'} [props.tone='warning'] - color scheme.
 *   ``warning`` for the 80% banner, ``blocked`` for the 402 envelope.
 * @param {string} [props.headline] - override the default headline
 *   (e.g. "Run blocked — upgrade to keep going" for the 402 surface).
 * @param {string} [props.bodyOverride] - extra context line. Default
 *   is auto-computed from the plan catalog.
 * @param {boolean} [props.allowed=true] - parent-side RBAC gate
 *   (typically the same predicate as ``OrgCreditRing``). Hidden when
 *   false.
 * @param {string} [props.successPath] - returned-to path on Stripe
 *   success; defaults to the current page.
 * @param {string} [props.cancelPath] - returned-to path on cancel.
 * @param {Array<{ ym: string, points: number, isCurrentMonth: boolean }>}
 *   [props.usageHistory] - per-month points totals for this org. When
 *   present we render a ``buildHistoryPreview`` line so the upgrade
 *   decision is grounded in the user's own data, not generic copy.
 *   Empty / omitted = bootstrap path (plan-spec copy only).
 */
export default function UpgradeCard({
  currentPlanKey,
  tone = 'warning',
  headline,
  bodyOverride,
  allowed = true,
  successPath,
  cancelPath,
  usageHistory,
}) {
  const { showToast } = useToast();
  const [busy, setBusy] = useState(false);
  const next = nextFiniteTierAfter(currentPlanKey);

  const handleUpgrade = useCallback(async () => {
    if (!next || busy) return;
    setBusy(true);
    try {
      const url = await createSubscriptionCheckout(next.key, {
        successPath: successPath ?? window.location.pathname,
        cancelPath: cancelPath ?? window.location.pathname,
      });
      // Same-tab redirect — Checkout becomes the canonical "buying"
      // flow and the user lands back on success_url after payment.
      // Different from Portal, which we open in a new tab to preserve
      // workbench context.
      window.location.assign(url);
    } catch (err) {
      showToast(
        err?.message || 'Could not start upgrade — try again in a moment.',
        'error',
      );
      setBusy(false);
    }
  }, [busy, next, successPath, cancelPath, showToast]);

  // Billing UI master switch (single-account/no-Stripe deploys set it false).
  if (!isBillingUiEnabled() || !allowed || !next) return null;

  const palette = TONE[tone] ?? TONE.warning;
  const currentPlan = GENERATED_PLAN_CATALOG.find(
    (p) => p.key === (currentPlanKey || '').toLowerCase(),
  );

  const computedHeadline =
    headline
    ?? (tone === 'blocked'
      ? `Run blocked — upgrade to ${next.name} to keep going`
      : `Upgrade to ${next.name} for more headroom`);

  const priceLabel = formatPriceCents(next.price_cents);
  const headroomLabel = `${formatPoints(next.points_per_period)} pts / month`;
  const computedBody =
    bodyOverride
    ?? (currentPlan
      ? `${next.name} includes ${headroomLabel} (vs. ${formatPoints(
          currentPlan.points_per_period,
        )} pts on ${currentPlan.name}). Switch instantly — Stripe credits the unused portion of this period.`
      : `${next.name} includes ${headroomLabel}. Switch instantly — Stripe credits the unused portion of this period.`);

  // PR 6g — actual-usage preview line. Only renders when caller passed
  // ``usageHistory``. Empty / single-month / over-budget all render
  // distinct copy through ``buildHistoryPreview``.
  const historyPreview = buildHistoryPreview(usageHistory, next);

  return (
    <div
      role="region"
      aria-label="Upgrade plan suggestion"
      className={`rounded-lg border px-4 py-3 ${palette.wrap}`}
    >
      <p className={`text-sm font-semibold ${palette.title}`}>{computedHeadline}</p>
      <p className={`mt-1 text-xs ${palette.body}`}>{computedBody}</p>
      {historyPreview ? (
        <p
          className={`mt-1 text-xs tabular-nums ${palette.body}`}
          data-testid="upgrade-card-history-preview"
        >
          {historyPreview}
        </p>
      ) : null}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={handleUpgrade}
          disabled={busy}
          className={palette.btn}
          aria-label={`Upgrade to ${next.name}${priceLabel ? ` for ${priceLabel} per month` : ''}`}
        >
          {busy
            ? 'Opening checkout…'
            : `Upgrade to ${next.name}${priceLabel ? ` — ${priceLabel}/mo` : ''}`}
        </button>
      </div>
    </div>
  );
}
